import { FLOW, PROBLEM_KIND, inLine, pubLabel } from "./flowCopy";
import { shortDate } from "./shared";
import type {
  AccountPlanStatus,
  Persona,
  PlanActivityAccount,
  PlanActivityEvent,
} from "../../Types/AccountPlan";
import { STALLED_LINE, stateChip } from "../../Utils/AccountPlans/queueText";

/**
 * The statuses of the Plans list (redesign, 2026-10-29; Queued split out of In
 * progress, Sushant 2026-11-04): every account sits in exactly one, so the status
 * bar adds up to the portfolio.
 * - action: the next step is the viewer's (fix an upload, confirm, verify, publish);
 * - queued_parse / queued_brief: waiting its turn in the reading or the briefing
 *   queue, with its place in that queue ("2nd of 10 in line");
 * - progress: the system is working, or the next step is someone else's;
 * - review: a draft briefing is being verified, and nothing is waiting on the viewer;
 * - published: the sponsor reads the current plan;
 * - none: no plan has been uploaded.
 */
export type PlanBucket =
  | "action"
  | "queued_parse"
  | "queued_brief"
  | "progress"
  | "review"
  | "published"
  | "none";

export const BUCKETS: PlanBucket[] = [
  "action",
  "queued_parse",
  "queued_brief",
  "progress",
  "review",
  "published",
  "none",
];

export const BUCKET_LABEL: Record<PlanBucket, string> = {
  action: "Action needed",
  queued_parse: "Queued for parsing",
  queued_brief: "Queued for briefing",
  progress: "In progress",
  review: "In review",
  published: "Published",
  none: "No plan",
};

/** Pill, dot and bar colours per status (pairs differ in lightness, not hue alone). */
export const BUCKET_STYLE: Record<
  PlanBucket,
  { pill: string; dot: string; bar: string }
> = {
  action: {
    pill: "bg-amber-50 text-amber-800",
    dot: "bg-amber-500",
    bar: "bg-amber-400",
  },
  queued_parse: {
    pill: "bg-slate-100 text-slate-700",
    dot: "bg-slate-500",
    bar: "bg-slate-400",
  },
  queued_brief: {
    pill: "bg-sky-50 text-sky-700",
    dot: "bg-sky-500",
    bar: "bg-sky-300",
  },
  progress: {
    pill: "bg-blue-50 text-blue-700",
    dot: "bg-blue-500",
    bar: "bg-blue-400",
  },
  review: {
    pill: "bg-purple-50 text-[#7E22CE]",
    dot: "bg-[#9333EA]",
    bar: "bg-purple-400",
  },
  published: {
    pill: "bg-emerald-50 text-emerald-700",
    dot: "bg-emerald-500",
    bar: "bg-emerald-500",
  },
  none: {
    pill: "bg-gray-100 text-gray-600",
    dot: "bg-gray-400",
    bar: "bg-gray-300",
  },
};

/** What the row's one button does. */
export type RowActionKind =
  | "upload" // no plan yet, or a new version
  | "fix" // the upload dialog for a failed, rejected or flagged version
  | "confirm" // Confirm plan: the AE accepts the read upload (Phase 2)
  | "draft" // Review draft: "Try again" on a confirmed version's failed briefing
  | "verify" // Review draft
  | "publish" // Publish
  | "briefing" // the published briefing, read-only
  | "open"; // the account at its current step

export interface RowAction {
  label: string;
  kind: RowActionKind;
  /** The filled button: the step is the viewer's. */
  primary: boolean;
  /** Shown greyed out, with `why` as its tooltip (board 01: "View plan · No plan yet"). */
  disabled?: boolean;
  why?: string;
}

export interface PlanRowState {
  bucket: PlanBucket;
  /** One plain line under the pill. */
  detail: string;
  /** The step-specific next action (the panel's button: "Verify", "Publish"...). */
  action: RowAction | null;
  /** The detail line says why the newest upload didn't become the plan. */
  problem: boolean;
  /**
   * The Plans list's one button (upload flow P1, 2026-10-29; labels per board 01,
   * 2026-10-31): see rowButton. The status line already says what the action is.
   */
  button: RowAction | null;
  /**
   * Published, but past the organization's refresh interval (Phase 4): the detail
   * line says so in amber. Still the Published bucket; not an action.
   */
  overdue?: boolean;
}

/** "View plan", greyed out: there is nothing to read yet (board 01 step 1). */
export const NO_PLAN_BUTTON: RowAction = {
  label: FLOW.viewPlan,
  kind: "open",
  primary: false,
  disabled: true,
  why: FLOW.noPlanYet,
};

/**
 * The Action needed button names the viewer's own step (UI tour, 2026-11-03:
 * every row read "Take action", so nothing said what the click would do).
 */
export const STEP_BUTTON: Record<RowActionKind, string> = {
  upload: "Upload plan",
  fix: FLOW.goToProblem,
  confirm: FLOW.confirmPlan,
  draft: FLOW.writeBriefing,
  verify: FLOW.reviewBriefing,
  publish: FLOW.publish,
  briefing: "View briefing",
  open: FLOW.viewPlan,
};

/**
 * The list's button for a row, derived from its status and step (board 01, the
 * flow pictures; design note D41, D46): the step's own name (STEP_BUTTON) when the
 * step is the viewer's; "Add plan" for the AE with no plan, a greyed "View plan" for
 * everyone else and on a stopped first upload; "Update plan" for the AE on a
 * published plan with nothing happening; "View progress"
 * while an upload is on its way to Confirm plan with nothing published; "View
 * plan" once there is a plan to read.
 */
export const rowButton = (
  bucket: PlanBucket,
  action: RowAction | null,
  s?: AccountPlanStatus | null,
  isAE = false
): RowAction | null => {
  if (bucket === "action" && action)
    return {
      label: STEP_BUTTON[action.kind],
      kind: action.kind,
      primary: true,
    };
  if (bucket === "none")
    return action
      ? { label: FLOW.addPlan, kind: "upload", primary: false }
      : NO_PLAN_BUTTON;
  // D41: the AE's published plan with nothing happening → Update plan (the upload
  // step); a new version on its way → View plan, as for everyone.
  if (isAE && bucket === "published")
    return { label: FLOW.updatePlan, kind: "upload", primary: false };
  const f = s?.latest_file;
  // A first upload that stopped (couldn't be read, or a check stopped it), with
  // nothing published: there is no plan to view yet (D41 "none with … a stopped
  // first upload", greyed per D46).
  const stoppedFirst =
    !!f &&
    !s?.published &&
    f.confirmed !== true &&
    (!!f.problem ||
      f.status === "failed" ||
      f.status === "rejected" ||
      f.status === "needs_review");
  if (stoppedFirst) return NO_PLAN_BUTTON;
  const beforeConfirm = !!f && f.confirmed === false && !s?.published;
  return {
    label: beforeConfirm ? FLOW.viewProgress : FLOW.viewPlan,
    kind: "open",
    primary: false,
  };
};

/**
 * The newest upload has been read and waits on Confirm plan (Phase 2). Only an
 * explicit `confirmed: false` counts: rows from before the Confirm step don't stall.
 */
export const awaitingConfirm = (s?: AccountPlanStatus | null): boolean => {
  const f = s?.latest_file;
  return (
    !!f &&
    f.confirmed === false &&
    (f.status === "ready" || f.status === "partial") &&
    !f.problem &&
    (!s?.review_status || s.review_status === "none")
  );
};

/** When the backend doesn't send a reason yet: plain words, no version number. */
const FALLBACK_PROBLEM: Record<string, string> = {
  failed: "Couldn't read the newest upload",
  rejected: "The newest upload needs review",
  needs_review: "The newest upload needs your OK",
};

const sections = (n: number) => `${n} section${n === 1 ? "" : "s"}`;

const pendingFor = (s: AccountPlanStatus, role: "AE" | "CSM"): number =>
  s.progress?.[role]?.pending?.length || 0;

/**
 * Where one account's plan stands for a viewer holding `roles` on it (AE and/or
 * CSM; an admin holds both). "Action needed" is the viewer's own work only
 * (decision 2026-10-29): what waits on someone else reads "Waiting on the AE".
 */
export const planRowState = (
  s: AccountPlanStatus,
  roles: Persona[]
): PlanRowState => {
  const base = rowState(s, roles);
  // Phase 3: the AE's last upload was refused before reading. It wasn't used, so it
  // never sets the status (UI tour A item 2): the row reads as the plan does, and
  // the line adds that the last upload wasn't used. Others' rows don't change.
  const r =
    s.last_rejection && roles.includes("AE")
      ? { ...base, detail: `${base.detail} · ${FLOW.lastUploadNotUsed}` }
      : base;
  return {
    ...r,
    problem: !!r.problem,
    overdue: !!r.overdue,
    button: rowButton(r.bucket, r.action, s, roles.includes("AE")),
  };
};

type BaseRowState = Pick<PlanRowState, "bucket" | "detail" | "action"> & {
  problem?: boolean;
  overdue?: boolean;
};

const rowState = (s: AccountPlanStatus, roles: Persona[]): BaseRowState => {
  const isAE = roles.includes("AE");
  const isCSM = roles.includes("CSM");
  const open: RowAction = { label: "Open", kind: "open", primary: false };
  const f = s.latest_file;

  // Not on the account (P7): a plan exists but isn't published, and that is all
  // they're told — no step, count, uploader or activity.
  if (!f && s.unpublished)
    return { bucket: "progress", detail: FLOW.notPublishedYet, action: null };

  if (!f)
    return {
      bucket: "none",
      // D16: the line doesn't repeat the chip; anyone else waits on the AE.
      detail: isAE ? "No plan uploaded yet" : FLOW.waitingAeUpload,
      action: isAE
        ? { label: "Upload plan", kind: "upload", primary: true }
        : null,
    };

  // An older published briefing the sponsor still reads while a newer plan moves on.
  // A stopped upload never became a version, so any published one is still live.
  // "Is the newest upload the published one?" compares the two INTERNAL upload
  // numbers (the publish record's `version` and the file's); the plan number is
  // only what the line shows (D1).
  const stillPublished =
    s.published && (!!f.problem || s.published.version !== f.version)
      ? ` · ${pubLabel(s.published)} still published`
      : "";

  // The newest upload didn't become the plan: say why in plain words (its own
  // version number is going away, so never "v4 needs fixing").
  if (
    f.problem ||
    f.status === "failed" ||
    f.status === "rejected" ||
    f.status === "needs_review"
  ) {
    const p = f.problem;
    const what =
      p?.reason ||
      FALLBACK_PROBLEM[f.status] ||
      (p ? PROBLEM_KIND[p.state] : "Needs review");
    const fix: RowAction = {
      label: FLOW.goToProblem,
      kind: "fix",
      primary: isAE,
    };
    if (isAE)
      return {
        bucket: "action",
        detail: `${what}${stillPublished}`,
        action: fix,
        problem: true,
      };
    // Q9 (Sushant, 2026-10-31): a stopped FIRST upload, nothing published: anyone
    // but the AE sees only that the AE is on it, never the reason or the file.
    if (!s.published)
      return {
        bucket: "progress",
        detail: FLOW.aeReviewingUpload,
        action: open,
      };
    return {
      bucket: "progress",
      detail: `Waiting on the AE · ${what}${stillPublished}`,
      action: fix,
      problem: true,
    };
  }

  if (f.status === "processing" || !s.viewer_ready) {
    const q = f.status === "processing" ? f.queue : null;
    // D40 (Sushant, 30 Oct): the row names the step, "parsing" not "reading", and no
    // place in line or wait estimate (deferred). A stalled job keeps its own line.
    const label = q?.stalled
      ? stateChip(q.state === "queued" ? "in_line" : "reading", { read: q })
          .label
      : q?.state === "queued"
        ? FLOW.queuedForParsing
        : FLOW.parsing;
    if (q?.state === "queued" && !q.stalled)
      return {
        bucket: "queued_parse",
        detail: `${inLine(q.ahead, q.in_line)}${stillPublished}`,
        action: open,
      };
    return {
      bucket: "progress",
      detail: `${label}${stillPublished}`,
      action: open,
    };
  }

  if (s.briefing_queue) {
    const q = s.briefing_queue;
    // D40: "Briefing waiting in line" / "Writing the briefing for v1", no estimate.
    const n = f.plan_version != null ? `v${f.plan_version}` : "the plan";
    const label = q.stalled
      ? stateChip(q.state === "queued" ? "brief_in_line" : "briefing", {
          brief: q,
        }).label
      : q.state === "queued"
        ? FLOW.briefingQueued
        : `${FLOW.writingBriefingFor} ${n}`;
    if (q.state === "queued" && !q.stalled)
      return {
        bucket: "queued_brief",
        detail: `${inLine(q.ahead, q.in_line)}${stillPublished}`,
        action: open,
      };
    return {
      bucket: "progress",
      detail: `${label}${stillPublished}`,
      action: open,
    };
  }

  const rs = s.review_status;
  // Confirm plan (Phase 2): read, and nothing is written until the AE accepts it.
  // Only an explicit false counts, so rows from before the Confirm step don't stall.
  if (awaitingConfirm(s)) {
    if (isAE)
      return {
        bucket: "action",
        detail: `${FLOW.confirmNewUpload}${stillPublished}`,
        action: { label: FLOW.confirmThePlan, kind: "confirm", primary: true },
      };
    return {
      bucket: "progress",
      detail: `${FLOW.waitingConfirmUpload}${stillPublished}`,
      action: open,
    };
  }

  if (!rs || rs === "none") {
    // Confirmed: the briefing starts on confirm, so no briefing means it is being
    // written or it didn't finish. There is no first-time "Draft briefing" any more.
    // null: confirmed before the Confirm step started the briefing (rare); the AE
    // starts it the same way as after a failure.
    if (s.sponsor_view_status === "processing")
      return {
        bucket: "progress",
        // A stalled briefing (pending_draft.state, UI tour A item 1) isn't being written.
        detail: `${
          s.pending_draft?.state === "briefing_stalled"
            ? STALLED_LINE
            : "Writing the briefing"
        }${stillPublished}`,
        action: open,
      };
    // P8 (Sushant, 2026-11-03): no "Briefing failed · Try again"; a briefing that can't be written retries, then reads as stalled.
    if (s.sponsor_view_status === "failed")
      return {
        bucket: "progress",
        detail: `${STALLED_LINE}${stillPublished}`,
        action: open,
      };
    // F-UF-F12: null is a version confirmed before the Confirm step started the
    // briefing: nothing failed, it just hasn't been written yet.
    const what = "hasn't been written yet";
    if (isAE)
      return {
        bucket: "action",
        detail: `The briefing ${what}${stillPublished}`,
        action: {
          label: "Write it",
          kind: "draft",
          primary: true,
        },
      };
    return {
      bucket: "progress",
      detail: `Waiting on the AE · the briefing ${what}${stillPublished}`,
      action: open,
    };
  }

  if (rs === "draft" || rs === "in_review") {
    const p = s.progress;
    const verified = p ? p.AE.verified + p.CSM.verified : 0;
    const owned = p ? p.AE.owned + p.CSM.owned : 0;
    const count = owned ? `${verified} of ${owned} verified` : "Draft ready";
    const mine =
      (isAE ? pendingFor(s, "AE") : 0) + (isCSM ? pendingFor(s, "CSM") : 0);
    if (mine > 0)
      return {
        bucket: "action",
        detail: `${sections(mine)} waiting for you to verify${stillPublished}`,
        action: { label: "Verify", kind: "verify", primary: true },
      };
    // The AE's own sections gate Publish; the CSM's are recommended only.
    if (isAE)
      return {
        bucket: "action",
        detail: `Ready to publish · ${count}${stillPublished}`,
        action: { label: "Publish", kind: "publish", primary: true },
      };
    const waitingOn = !p
      ? ""
      : pendingFor(s, "AE")
        ? " · waiting on the AE"
        : pendingFor(s, "CSM")
          ? " · waiting on the CSM"
          : ` · ${FLOW.waitingAePublish}`;
    return {
      bucket: "review",
      detail: `${count}${waitingOn}${stillPublished}`,
      action: open,
    };
  }

  if (rs === "published" && s.published && s.freshness?.overdue)
    return {
      bucket: "published",
      detail: `${
        s.freshness.published_plan_version != null
          ? `v${s.freshness.published_plan_version}`
          : pubLabel(s.published)
      } · due for a refresh`,
      action: { label: "View briefing", kind: "briefing", primary: false },
      overdue: true,
    };

  if (rs === "published" && s.published)
    return {
      bucket: "published",
      detail: `Sponsor can read it · ${shortDate(s.published.at)}`,
      action: { label: "View briefing", kind: "briefing", primary: false },
    };

  return { bucket: "review", detail: "Draft ready", action: open };
};

/** Action needed first, then the order of the status bar; by name within a status. */
export const bucketRank = (b: PlanBucket): number => BUCKETS.indexOf(b);

/** What a person did last on an account's plans: the list's Last Activity cell. */
export interface LastActivity {
  at: string;
  by: string | null;
  /** The action, as a short sentence: "Verified 2 sections", "Published v2". */
  what: string;
}

/**
 * Activity events a person causes. Everything else (reading started or finished,
 * a check failing, the briefing written) is the system's and never counts as
 * Last Activity: it would show "Ana · just now" for work Ana didn't do.
 */
export const PERSON_EVENTS = [
  "uploaded",
  "confirmed",
  "overridden",
  "retried",
  "withdrawn",
  "deleted",
  "discarded",
  "restored",
  "upload_rejected",
  "rejection_dismissed",
  "moved_away",
  "briefing_requested",
  "verified",
  "published",
] as const;

/** A person's event as the action they took; "" for system events. `version` is the plan number (D1). */
export const personAction = (
  e: PlanActivityEvent,
  version: number | null
): string => {
  const n = Number(e.detail?.sections) || 0;
  switch (e.event) {
    case "uploaded":
      return "Uploaded a file";
    case "confirmed":
      return version != null ? `Confirmed v${version}` : "Confirmed the plan";
    case "overridden":
      return "Used a flagged upload anyway";
    case "retried":
      return "Tried reading again";
    case "withdrawn":
      return "Withdrew an upload";
    case "deleted":
      return "Deleted an upload";
    case "discarded":
      return "Discarded an upload";
    case "restored":
      return "Restored an upload";
    case "upload_rejected":
      return "Upload rejected";
    case "rejection_dismissed":
      return "Dismissed a rejection";
    case "moved_away":
      return "Moved an upload to another account";
    case "briefing_requested":
      return "Drafted the briefing";
    case "verified":
      return n ? `Verified ${sections(n)}` : "Verified sections";
    case "published":
      return version != null ? `Published v${version}` : "Published";
    default:
      return "";
  }
};

/** The newest event by a person across all of an account's versions. */
export const lastPersonActivity = (
  a?: PlanActivityAccount | null
): LastActivity | null => {
  if (!a) return null;
  const isPerson = (ev: string) =>
    (PERSON_EVENTS as readonly string[]).indexOf(ev) >= 0;
  const best = [a.latest, ...(a.earlier || [])]
    .flatMap(r =>
      // The plan number (D1), never the internal upload number.
      (r?.events || []).map(e => ({ e, version: r?.plan_version ?? null }))
    )
    .filter(x => !!x.e.at && isPerson(x.e.event))
    .sort((x, y) => (y.e.at || "").localeCompare(x.e.at || ""))[0];
  if (!best) return null;
  return {
    at: best.e.at as string,
    by: best.e.actor_name,
    what: personAction(best.e, best.version),
  };
};

/**
 * Phase 4: the server's own Last Activity (the newest action by a person,
 * account-level events such as a refused upload included). Null when absent.
 */
export const lastActivityFromServer = (
  s?: AccountPlanStatus
): LastActivity | null => {
  const la = s?.last_activity;
  if (!la || !la.at) return null;
  return { at: la.at, by: la.actor_name || null, what: la.label };
};

/**
 * Before Activity loads (or for an account it doesn't cover), the status still
 * knows two things people did: the newest upload and the publish.
 */
export const lastActivityFromStatus = (
  s?: AccountPlanStatus
): LastActivity | null => {
  const out: LastActivity[] = [];
  if (s?.published?.at)
    out.push({
      at: s.published.at,
      by: s.published.by_name || null,
      what: `Published ${pubLabel(s.published)}`,
    });
  if (s?.latest_file?.created_at)
    out.push({
      at: s.latest_file.created_at,
      by: null,
      what: "Uploaded a file",
    });
  out.sort((x, y) => new Date(y.at).getTime() - new Date(x.at).getTime());
  return out[0] || null;
};
